<script setup>
import { Button, Menu } from '@dashhudson/dashing-ui';
import { computed, onBeforeUnmount, ref, toRefs, watch } from 'vue';
import { useAddToGalleryAction } from '@/app/library/composables/useAddToGalleryAction';
import { useAddToCampaignAction } from '@/app/campaigns/composables/useAddToCampaignAction';
import { useAddToActionMenuStore } from '@/stores/add-to-action-menu';
import { constants } from '@/config';
import { storeToRefs } from 'pinia';

const props = defineProps({
  selectedMedia: { type: Array, required: true },
  isCreatorMedia: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  tooltip: { type: String, default: 'Add to' },
});

const emit = defineEmits(['onActionComplete', 'mediaAddedToCollection']);

const { selectedMedia } = toRefs(props);

const addToActionMenuStore = useAddToActionMenuStore();
const { selectedCampaigns, selectedGalleriesMap } = storeToRefs(addToActionMenuStore);

// use ref + watcher to avoid 'no-use-before-define' error
const selectedCollections = ref([]);

function onActionComplete() {
  emit('mediaAddedToCollection', selectedCollections.value);
  addToActionMenuStore.reset();
  emit('onActionComplete');
}

const { galleryChannelOptions } = useAddToGalleryAction(selectedMedia, onActionComplete);
const { addToCampaignOption } = useAddToCampaignAction(
  selectedMedia,
  props.isCreatorMedia,
  onActionComplete,
);

const addToOptions = computed(() => {
  const combinedOptions = [addToCampaignOption.value, ...galleryChannelOptions.value];

  return combinedOptions.filter((option) => !option.hide);
});

watch(
  [selectedGalleriesMap, selectedCampaigns, addToOptions],
  () => {
    const selected = Object.keys(selectedGalleriesMap.value || {});
    if (selectedCampaigns.value?.length > 0) {
      selected.push(constants.CAMPAIGNS);
    }
    selectedCollections.value = addToOptions.value
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  addToActionMenuStore.reset();
});
</script>

<template>
  <Menu :model="addToOptions" :dropdown="{ placement: 'bottom-end' }">
    <Button
      v-dash-tooltip="props.tooltip"
      size="xs"
      severity="bulk"
      icon="folder-plus"
      :disabled="props.disabled"
    />
  </Menu>
</template>
